import { Breadcrumb, Spin } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

import './Breadcrumb.css';

const useTitleObserver = () => {
  const [title, setTitle] = useState(document.title);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTitle(document.title);
    });

    const config = { subtree: true, characterData: true, childList: true };
    observer.observe(document.querySelector('title')!, config);

    return () => observer.disconnect();
  }, []);

  return title;
};

const trimHistory = (history: { title: string; path: string }[]) => {
  return history.slice(-3);
};

const HistoryBreadcrumb = () => {
  const title = useTitleObserver();
  const location = useLocation();
  const isFirstRender = useRef(true);
  const [history, setHistory] = useState<{ title: string; path: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const routes = window.localStorage.getItem('routes');
    const parsedRoutes = routes ? JSON.parse(routes) : [];
    setHistory(parsedRoutes);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const fullPath = `${location.pathname}${location.search}`;
    setLoading(true);

    setHistory((prev) => {
      if (prev.length > 0 && prev[prev.length - 1].path === fullPath) {
        setLoading(false);
        return prev;
      }

      const titleCounts = new Map<string, number>();
      for (const item of prev) {
        titleCounts.set(item.title, (titleCounts.get(item.title) || 0) + 1);
      }

      const firstDuplicateIndex = prev.findIndex(
        (item) => titleCounts.get(item.title)! > 1,
      );

      if (firstDuplicateIndex !== -1) {
        prev.splice(firstDuplicateIndex, 1);
      }

      const newHistory = [...prev, { title, path: fullPath }];
      const trimmedHistory = trimHistory(newHistory);
      window.localStorage.setItem('routes', JSON.stringify(trimmedHistory));

      setLoading(false);
      return trimmedHistory;
    });
  }, [location.pathname, location.search]);

  // Separate effect to update the last title change
  useEffect(() => {
    setLoading(true);

    setHistory((prev) => {
      if (prev.length === 0) {
        setLoading(false);
        return prev;
      }

      const updatedHistory = [...prev];
      updatedHistory[updatedHistory.length - 1].title = title;
      window.localStorage.setItem('routes', JSON.stringify(updatedHistory));

      setLoading(false);
      return updatedHistory;
    });
  }, [title]);

  return (
    <Spin spinning={loading}>
      <Breadcrumb style={{ marginBottom: 16 }} separator=">">
        {history.map(({ title, path }, index) => {
          const isCurrent = path === `${location.pathname}${location.search}`;
          return (
            <Breadcrumb.Item
              key={`${path}-${index}`}
              className={
                isCurrent ? 'breadcrumb-current' : 'breadcrumb-previous'
              }
            >
              {isCurrent ? title : <Link to={path}>{title}</Link>}
            </Breadcrumb.Item>
          );
        })}
      </Breadcrumb>
    </Spin>
  );
};

export default HistoryBreadcrumb;
